import { Box, Button, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";

import getTheme from "../../theme/theme";
import { PinnacleThemes } from "../../theme/types";
import { forgotPassword } from "../../api/services/auth";
import { handleApiError } from "../../utils/errorHandler";
import { EMAIL_REGEX } from "../../utils/emailValidate";
import { BTN_BACK_TO_LOGIN, BTN_CONFIRM, E_ENTER_EMAIL_ADDRESS, FORGOT_PASSWORD, FORM_FORGOT_PASSWORD_DESCRIPTION } from "../../utils/constants";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { blue, grey } from "@mui/material/colors";
import PinnacleFormContainer from "../../components/PinnacleFormContainer/PinnacleFormContainer";

export default function ForgotPasswordPage() {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const isEmailValid = EMAIL_REGEX.test(email);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (!emailTouched) setEmailTouched(true);
        if (errorMessage) setErrorMessage(null);
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailTouched(true);

        if (!isEmailValid) {
            return;
        }

        setFormDisabled(true);
        try {
            const forgotPasswordResponse = await forgotPassword({ email });
            pushNotification({
                details: forgotPasswordResponse.message,
                type: NotificationTypes.Success,
            });
        } catch (error) {
            const { errorMessage } = handleApiError(error);
            setErrorMessage(errorMessage);

        } finally {
            setFormDisabled(false);
        }
    };

    return (
        <PinnacleFormContainer
            formName={FORGOT_PASSWORD}
            formDescription={FORM_FORGOT_PASSWORD_DESCRIPTION}
        >
            <form onSubmit={handleSubmit} noValidate>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* EMAIL */}
                    <PinnacleTextField
                        error={emailTouched && (!isEmailValid || !!errorMessage)}
                        color={emailTouched && (!isEmailValid || !!errorMessage) ? 'error' : 'primary'}
                        helperText={
                            emailTouched && !isEmailValid
                                ? E_ENTER_EMAIL_ADDRESS
                                : errorMessage
                        }
                        id="email"
                        autoFocus
                        label="Email Address"
                        variant="outlined"
                        onChange={handleEmailChange}
                        onBlur={() => setEmailTouched(true)}
                        value={email}
                        autoComplete="email"
                        disabled={formDisabled}
                    />
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={!isEmailValid || formDisabled}
                        sx={{
                            mb: 1.5,
                            '&.Mui-disabled': {
                                backgroundColor: grey[800],
                                color: grey[400],
                                opacity: 1,
                            },
                        }}
                    >
                        {formDisabled ? (
                            <CircularProgress size={24} sx={{ color: blue[300] }} />
                        ) : (
                            BTN_CONFIRM
                        )}
                    </Button>
                    <Button
                        fullWidth
                        variant="text"
                        sx={{
                            borderColor: theme.palette.common.white,
                            color: theme.palette.common.white,
                        }}
                        onClick={() => navigate("/login")}
                    >
                        {BTN_BACK_TO_LOGIN}
                    </Button>
                </Box>
            </form>
        </PinnacleFormContainer>
    )
}